import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';
import { ArrowLeft, MagnifyingGlass } from '@phosphor-icons/react';
import useDocumentMeta from '@/hooks/useDocumentMeta';

export default function NotFoundPage() {
  const { t, language } = useLanguage();
  useDocumentMeta({
    title: language === 'tr' ? 'Sayfa Bulunamadı (404) — GrowthRIQ' : 'Page Not Found (404) — GrowthRIQ',
    description: language === 'tr'
      ? 'Aradığınız sayfa bulunamadı. GrowthRIQ ana sayfasına dönün veya ücretsiz araçlarımıza göz atın.'
      : 'The page you are looking for was not found. Return to the GrowthRIQ home page or explore our free tools.',
  });

  const isEn = language === 'en';
  const hubPath = isEn ? '/tools' : '/araclar';
  const blogPath = isEn ? '/en/blog' : '/blog';

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex flex-col" data-testid="not-found-page">
      <header className="px-6 py-5 border-b border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/" data-testid="nav-logo-home"><Logo variant="light" size="md" /></Link>
        </div>
      </header>
      <main className="flex-1 flex items-center px-6 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-[120px] font-extrabold leading-none tracking-tight text-[#0F1B36]" aria-hidden="true">
            4<span className="text-[#3CD9B8]">0</span>4
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F1B36] mt-4 mb-3">
            {isEn ? 'Page not found' : 'Sayfa bulunamadı'}
          </h1>
          <p className="text-[#52525B] leading-relaxed mb-8">
            {isEn
              ? "The page you're looking for doesn't exist or has been moved. Try one of the links below to get back on track."
              : 'Aradığınız sayfa mevcut değil ya da taşınmış. Aşağıdaki linklerden birini kullanarak devam edebilirsiniz.'}
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mb-8 text-left">
            <Link to="/" className="p-4 bg-white border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="404-home">
              <div className="text-xs uppercase tracking-wider text-[#71717A] mb-1">{isEn ? 'Home' : 'Ana sayfa'}</div>
              <div className="font-semibold text-[#0F1B36] group-hover:text-[#3CD9B8] flex items-center gap-1">
                GrowthRIQ <ArrowLeft size={14} weight="bold" className="rotate-180" />
              </div>
            </Link>
            <Link to={hubPath} className="p-4 bg-white border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="404-tools">
              <div className="text-xs uppercase tracking-wider text-[#71717A] mb-1">{isEn ? 'Free tools' : 'Ücretsiz araçlar'}</div>
              <div className="font-semibold text-[#0F1B36] group-hover:text-[#3CD9B8] flex items-center gap-1">
                {isEn ? '4 AI tools' : '4 AI aracı'} <ArrowLeft size={14} weight="bold" className="rotate-180" />
              </div>
            </Link>
            <Link to={blogPath} className="p-4 bg-white border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="404-blog">
              <div className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Blog</div>
              <div className="font-semibold text-[#0F1B36] group-hover:text-[#3CD9B8] flex items-center gap-1">
                {isEn ? 'Latest articles' : 'Son yazılar'} <ArrowLeft size={14} weight="bold" className="rotate-180" />
              </div>
            </Link>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F1B36] text-white font-semibold hover:bg-[#1C2F5F] transition-colors"
            data-testid="404-cta-home"
          >
            <MagnifyingGlass size={16} weight="bold" />
            {isEn ? 'Back to GrowthRIQ' : "GrowthRIQ'ya dön"}
          </Link>
        </div>
      </main>
    </div>
  );
}
